/**
 * Etichette e colori degli step LLM (`llm_calls.step`), condivisi tra il
 * pannello "Consumi LLM" (AdminCosts) e il dettaglio pratica (RequestDetail).
 *
 * Prima ogni pagina aveva la sua mappa: il dettaglio conosceva solo i 4 step
 * legacy e mostrava `ddt_graph` grezzo. La lista qui sotto e' allineata agli
 * step emessi dal backend:
 *   grep -rhoE 'pipeline_step\("[a-z_]+"|step(_name)?="[a-z_]+"' src/ | sort -u
 */
import type { CostCategory } from "../api/types";

const STEP_LABELS: Record<string, string> = {
  // Pipeline legacy (email → ticket)
  classify: "Classificazione",
  extract: "Estrazione",
  analyze: "Analisi",
  match: "Matching",
  v3: "Pipeline v3",
  // DDT
  ddt_detect_documents: "DDT · rilevamento documenti",
  ddt_ocr: "DDT · OCR",
  ddt_graph: "DDT · grafo",
  ddt_reprocess: "DDT · riprocesso",
  // BSC
  bsc: "BSC",
  bsc_graph: "BSC · grafo",
  bsc_update: "BSC · aggiornamento",
  bsc_reprocess: "BSC · riprocesso",
  // Rinascente
  rinascente: "Rinascente",
  rinascente_ocr: "Rinascente · OCR",
  rinascente_reprocess: "Rinascente · riprocesso",
  // Agente conversazionale, deleghe, audio
  conversation_turn: "Conversazione · turno",
  conversation_summary: "Conversazione · riepilogo",
  analyze_delegation: "Delega · analisi",
  transcription: "Trascrizione",
  unknown: "Altro",
};

/** Step della revisione manuale BSC: `bsc_manual_document`, `bsc_manual_upload`, ... */
const BSC_MANUAL_PREFIX = "bsc_manual_";

/** Colori storici dei 4 step legacy: restano fissi, cosi' un grafico letto ieri si legge anche oggi. */
const STEP_COLORS: Record<string, string> = {
  classify: "var(--indaco-brand-primary)",
  extract: "var(--indaco-success)",
  analyze: "var(--indaco-warning)",
  match: "var(--indaco-chart-match)",
  unknown: "var(--indaco-gray-600)",
};

/**
 * Tinte categoriche del DS per gli altri step (DDT, BSC, Rinascente, ...):
 * prima erano tutti grigio "unknown" e la torta dei costi usciva monocolore,
 * con la legenda inutilizzabile. Solo token Indaco: niente esadecimali qui.
 */
export const CHART_PALETTE = [
  "var(--indaco-brand-primary)",
  "var(--indaco-success)",
  "var(--indaco-warning)",
  "var(--indaco-chart-match)",
  "var(--indaco-danger)",
  "var(--indaco-new-entity)",
  "var(--indaco-brand-dark)",
  "var(--indaco-gray-700)",
];

/**
 * Scala colore per un insieme di step, assegnata nell'ordine dei dati (di
 * solito per costo decrescente, quindi le fette grandi sono le prime servite):
 * uno step legacy tiene il suo colore storico se e' ancora libero, tutti gli
 * altri prendono la prima tinta non usata. Otto step distinti = otto colori;
 * oltre si ricicla. Torta e tabella dello stesso dataset condividono la scala,
 * cosi' lo stesso step ha lo stesso colore in entrambe.
 */
export function stepColorScale(steps: (string | null | undefined)[]): (step: string | null | undefined) => string {
  const assigned = new Map<string, string>();
  const taken = new Set<string>();
  for (const s of steps) {
    if (!s || assigned.has(s)) continue;
    const preferred = STEP_COLORS[s];
    const color = preferred && !taken.has(preferred)
      ? preferred
      : CHART_PALETTE.find((c) => !taken.has(c)) ?? preferred ?? CHART_PALETTE[assigned.size % CHART_PALETTE.length];
    assigned.set(s, color);
    taken.add(color);
  }
  return (step) => (step && assigned.get(step)) || STEP_COLORS.unknown;
}

/** Step assente (`null`/vuoto) = chiamata non attribuita a uno step: trattino. */
const NO_STEP = "—";

export function stepLabel(step: string | null | undefined): string {
  if (!step) return NO_STEP;
  const known = STEP_LABELS[step];
  if (known) return known;
  if (step.startsWith(BSC_MANUAL_PREFIX)) {
    const detail = step.slice(BSC_MANUAL_PREFIX.length).replace(/_/g, " ");
    return detail ? `BSC · manuale (${detail})` : "BSC · manuale";
  }
  // Step sconosciuto: meglio il nome tecnico che un "Altro" che nasconde il dato.
  return step;
}

/* ── Categorie di costo di un ticket ──────────────────────────────────────────
 *
 * Il costo di un ticket nato da conversazione comprende il dialogo che l'ha
 * prodotto (i turni dell'agente portano il `request_id` della pratica): e' il
 * numero giusto per "quanto e' costato questo risultato" e quello sbagliato
 * per "costa piu' di quello arrivato via email", dove il dialogo non esiste.
 * Da qui la scomposizione: le due domande hanno due risposte, e la pagina
 * mostra entrambe.
 *
 * Le chiavi e l'ordine sono quelli del backend (`_CATEGORY_ORDER` in
 * `src/api/routes/admin_costs.py`, invariante 8): qui ci sono solo le
 * ETICHETTE e i COLORI, non una seconda tassonomia. Se il backend aggiunge una
 * categoria, `categoryLabel` mostra la chiave grezza invece di nasconderla —
 * come fa `stepLabel` per uno step sconosciuto.
 */


/** Ordine di presentazione = ordine dei segmenti della barra, uguale su ogni
 *  riga: due ticket si confrontano a colpo d'occhio solo se i segmenti stanno
 *  sempre nello stesso posto. */
export const CATEGORY_ORDER: CostCategory[] = [
  "dialogo",
  "acquisizione",
  "analisi",
  "lavorazione",
  "altro",
];

const CATEGORY_LABELS: Record<CostCategory, string> = {
  dialogo: "Dialogo",
  acquisizione: "Acquisizione",
  analisi: "Analisi",
  lavorazione: "Lavorazione",
  altro: "Altro",
};

/** Che cosa c'e' dentro una categoria: va nei `title`, perche' "Acquisizione"
 *  da solo non dice che li' dentro ci sono OCR e trascrizione. */
const CATEGORY_HINTS: Record<CostCategory, string> = {
  dialogo: "Turni dell'agente conversazionale che hanno generato la pratica (conversation_turn)",
  acquisizione: "OCR e trascrizione: costo che scala con le pagine dell'allegato e i minuti dell'audio",
  analisi: "Analisi della richiesta (analyze, analyze_delegation)",
  lavorazione: "Il resto della pipeline: classificazione, estrazione, matching, grafi DDT/BSC/Rinascente, revisione manuale",
  altro: "Step senza una categoria dichiarata (compreso il costo di chiamate senza step): resta nel totale, non finisce in Analisi",
};

/**
 * Colori delle categorie. `analisi` tiene il giallo storico dello step
 * `analyze` (vedi `STEP_COLORS`) e `altro` il grigio di `unknown`: la stessa
 * cosa non cambia colore passando dalla torta degli step alla barra delle
 * categorie. Solo token Indaco, niente esadecimali.
 */
const CATEGORY_COLORS: Record<CostCategory, string> = {
  dialogo: "var(--indaco-brand-primary)",
  acquisizione: "var(--indaco-success)",
  analisi: "var(--indaco-warning)",
  lavorazione: "var(--indaco-chart-match)",
  altro: "var(--indaco-gray-600)",
};

/** Etichetta di una categoria; una chiave non prevista si mostra grezza. */
export function categoryLabel(c: string): string {
  return CATEGORY_LABELS[c as CostCategory] ?? c;
}

/** Spiegazione di una categoria (per i `title`); stringa vuota se sconosciuta. */
export function categoryHint(c: string): string {
  return CATEGORY_HINTS[c as CostCategory] ?? "";
}

/** Colore di una categoria; una chiave non prevista prende il grigio di "altro". */
export function categoryColor(c: string): string {
  return CATEGORY_COLORS[c as CostCategory] ?? CATEGORY_COLORS.altro;
}
